'use client';

import { useCallback, useMemo, useState } from 'react';
import { FileIcon, Upload, X } from 'lucide-react';
import { useDropzone, type Accept, type FileRejection } from 'react-dropzone';
import { useTranslation } from 'react-i18next';

import { Button } from '@kit/ui/button';
import { cn } from '@kit/ui/utils';

import { isImageFile } from '../utils/file-type-utils';

interface DocumentDropzoneProps {
  onFilesSelected: (files: File[]) => void;
  acceptedTypes?: Accept;
  maxFiles?: number;
  maxSize?: number;
  disabled?: boolean;
  children?: React.ReactNode;
  className?: string;
  showPreview?: boolean;
}

interface FilePreview {
  file: File;
  previewUrl: string | null;
}

/**
 * Drag-drop zone for file selection with preview support
 *
 * @example
 * ```tsx
 * <DocumentDropzone
 *   onFilesSelected={(files) => console.log(files)}
 *   acceptedTypes={{
 *     'image/*': ['.jpg', '.jpeg', '.png', '.webp'],
 *     'application/pdf': ['.pdf'],
 *   }}
 *   maxFiles={5}
 *   maxSize={50 * 1024 * 1024} // 50MB
 *   showPreview
 * />
 * ```
 */
export function DocumentDropzone({
  onFilesSelected,
  acceptedTypes,
  maxFiles = 1,
  maxSize = 50 * 1024 * 1024, // 50MB default
  disabled = false,
  children,
  className,
  showPreview = false,
}: DocumentDropzoneProps) {
  const { t } = useTranslation();
  const [previews, setPreviews] = useState<FilePreview[]>([]);
  const [errors, setErrors] = useState<string[]>([]);

  const onDrop = useCallback(
    (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      setErrors([]);

      // Handle rejected files
      if (rejectedFiles.length > 0) {
        const newErrors: string[] = [];
        rejectedFiles.forEach((rejection) => {
          rejection.errors.forEach((error) => {
            if (error.code === 'file-too-large') {
              newErrors.push(
                t('documents:dropzone.fileTooLarge', {
                  fileName: rejection.file.name,
                  size: formatFileSize(maxSize),
                }),
              );
            } else if (error.code === 'file-invalid-type') {
              newErrors.push(
                t('documents:dropzone.fileTypeNotSupported', {
                  fileName: rejection.file.name,
                }),
              );
            } else if (error.code === 'too-many-files') {
              newErrors.push(
                t('documents:dropzone.tooManyFiles', { count: maxFiles }),
              );
            } else {
              newErrors.push(
                t('documents:dropzone.fileError', {
                  fileName: rejection.file.name,
                  message: error.message,
                }),
              );
            }
          });
        });
        setErrors(newErrors);
        return;
      }

      // Create previews for image files
      if (showPreview) {
        const newPreviews: FilePreview[] = acceptedFiles.map((file) => ({
          file,
          previewUrl: isImageFile(file.type) ? URL.createObjectURL(file) : null,
        }));

        // Revoke old preview URLs
        previews.forEach((preview) => {
          if (preview.previewUrl) {
            URL.revokeObjectURL(preview.previewUrl);
          }
        });

        setPreviews(newPreviews);
      }

      onFilesSelected(acceptedFiles);
    },
    [maxFiles, maxSize, onFilesSelected, previews, showPreview, t],
  );

  const { getRootProps, getInputProps, isDragActive, isDragReject } =
    useDropzone({
      onDrop,
      accept: acceptedTypes,
      maxFiles,
      maxSize,
      disabled,
      multiple: maxFiles > 1,
    });

  const removeFile = useCallback(
    (index: number) => {
      const newPreviews = [...previews];
      const removed = newPreviews.splice(index, 1)[0];
      if (removed?.previewUrl) {
        URL.revokeObjectURL(removed.previewUrl);
      }
      setPreviews(newPreviews);
      onFilesSelected(newPreviews.map((p) => p.file));
    },
    [previews, onFilesSelected],
  );

  const clearAll = useCallback(() => {
    previews.forEach((preview) => {
      if (preview.previewUrl) {
        URL.revokeObjectURL(preview.previewUrl);
      }
    });
    setPreviews([]);
    setErrors([]);
    onFilesSelected([]);
  }, [previews, onFilesSelected]);

  // Format accepted types for display
  const acceptedTypesDisplay = useMemo(() => {
    if (!acceptedTypes) return '';
    const extensions = Object.values(acceptedTypes).flat();
    return extensions.join(', ').toUpperCase().replace(/\./g, '');
  }, [acceptedTypes]);

  return (
    <div className={cn('space-y-3', className)}>
      {/* Drop zone */}
      <div
        {...getRootProps()}
        className={cn(
          'cursor-pointer rounded-lg border-2 border-dashed p-6 text-center transition-colors',
          'focus-visible:ring-ring focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none',
          isDragActive && !isDragReject && 'border-primary bg-primary/5',
          isDragReject && 'border-destructive bg-destructive/5',
          !isDragActive &&
            !isDragReject &&
            'border-muted-foreground/25 hover:border-primary/50',
          disabled && 'pointer-events-none opacity-50',
        )}
        tabIndex={disabled ? -1 : 0}
        role="button"
        aria-label={t('documents:dropzone.uploadFilesLabel')}
      >
        {/* eslint-disable-next-line kit-components/use-kit-primitives -- react-dropzone getInputProps() file input */}
        <input
          {...getInputProps()}
          aria-label={t('documents:dropzone.fileInputLabel')}
        />

        {children || (
          <div className="space-y-2">
            <Upload
              className={cn(
                'mx-auto h-10 w-10',
                isDragReject ? 'text-destructive' : 'text-muted-foreground',
              )}
            />
            <p className="text-muted-foreground text-sm">
              {isDragActive
                ? isDragReject
                  ? t('documents:dropzone.dropRejected')
                  : t('documents:dropzone.dropActive')
                : t('documents:dropzone.dropPrompt')}
            </p>
            <p className="text-muted-foreground text-xs">
              {acceptedTypesDisplay && `${acceptedTypesDisplay} • `}
              {t('documents:dropzone.maxSize', {
                size: formatFileSize(maxSize),
              })}
              {maxFiles > 1 &&
                ` • ${t('documents:dropzone.upToFiles', { count: maxFiles })}`}
            </p>
          </div>
        )}
      </div>

      {/* Errors */}
      {errors.length > 0 && (
        <div className="space-y-1">
          {errors.map((error, index) => (
            <p key={index} className="text-destructive text-sm">
              {error}
            </p>
          ))}
        </div>
      )}

      {/* Previews */}
      {showPreview && previews.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-muted-foreground text-sm">
              {t('documents:dropzone.filesSelected', {
                count: previews.length,
              })}
            </p>
            <Button
              type="button"
              variant="ghost"
              onClick={clearAll}
              className="text-muted-foreground hover:text-foreground text-sm underline"
              disabled={disabled}
            >
              {t('documents:dropzone.clearAll')}
            </Button>
          </div>
          <div className="grid gap-2">
            {previews.map((preview, index) => (
              <div
                key={`${preview.file.name}-${index}`}
                className="bg-muted/50 flex items-center justify-between rounded-lg border p-3"
              >
                <div className="flex items-center gap-3 overflow-hidden">
                  {preview.previewUrl ? (
                    // eslint-disable-next-line @next/next/no-img-element, ballee-media/no-untransformed-media -- blob: preview of a just-dropped file, never R2; cdnImageUrl would pass it through unchanged.
                    <img
                      src={preview.previewUrl}
                      alt={preview.file.name}
                      className="h-10 w-10 rounded object-cover"
                    />
                  ) : (
                    <FileIcon className="text-muted-foreground h-10 w-10 flex-shrink-0" />
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">
                      {preview.file.name}
                    </p>
                    <p className="text-muted-foreground text-xs">
                      {formatFileSize(preview.file.size)}
                    </p>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => removeFile(index)}
                  className="text-muted-foreground hover:text-foreground ml-2 flex-shrink-0 p-1"
                  disabled={disabled}
                  aria-label={t('documents:dropzone.removeFile', {
                    fileName: preview.file.name,
                  })}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
